import * as Yup from 'yup';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import { UserType } from '@src/v3/interfaces/components/registerModal';
import { NAME_PATTERN } from '@src/v3/constants/common';
import { VALIDATION_MESSAGES } from '@src/v3/constants/messages';

// Phone number validation schema
export const phoneValidationSchema = Yup.object({
  phone: Yup.string()
    .required('Phone number is required')
    .test(
      'phone-format',
      'Please enter a valid 10-digit phone number',
      (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, '');
        return digits.length === 10;
      }
    ),
});

// OTP code validation schema
export const otpValidationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Please enter a valid 6-digit code'),
});

// User type validation schema
export const userTypeValidationSchema = Yup.object({
  userType: Yup.string()
    .oneOf(
      [ZEME_USERS_ROLE.RENTER, ZEME_USERS_ROLE.AGENT, ZEME_USERS_ROLE.LANDLORD],
      'Please select a valid user type'
    )
    .required('User type is required'),
});

// User details validation schema
export const userDetailsValidationSchema = Yup.object({
  userType: Yup.string(),
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .matches(NAME_PATTERN, VALIDATION_MESSAGES.invalidCharacters),
  middleName: Yup.string()
    .max(50, 'Middle name must be less than 50 characters')
    .matches(NAME_PATTERN, VALIDATION_MESSAGES.invalidCharacters),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .matches(NAME_PATTERN, VALIDATION_MESSAGES.invalidCharacters),
  email: Yup.string().when('$isOAuthFlow', {
    is: true,
    then: (schema) =>
      schema
        .notRequired()
        .email('Please enter a valid email address')
        .max(100, 'Email must be less than 100 characters'),
    otherwise: (schema) =>
      schema
        .required('Email is required')
        .email('Please enter a valid email address')
        .max(100, 'Email must be less than 100 characters'),
  }),
  licenseNumber: Yup.string().when('userType', {
    is: (val: string) => val === ZEME_USERS_ROLE.AGENT,
    then: (schema) =>
      schema
        .required('License number is required')
        .max(100, 'License number must be less than 100 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
  phone: Yup.string().when('$isOAuthFlow', {
    is: true,
    then: (schema) =>
      schema
        .required('Phone number is required')
        .test(
          'phone-format',
          'Please enter a valid 10-digit phone number',
          (value) => {
            if (!value) return false;
            const digits = value.replace(/\D/g, '');
            return digits.length === 10;
          }
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  consent: Yup.boolean().oneOf(
    [true],
    'You must agree to the terms and conditions'
  ),
});

// Work details validation schema
export const workDetailsValidationSchema = Yup.object({
  companyName: Yup.string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  companyAddressLine1: Yup.string()
    .required('Company address is required')
    .min(5, 'Company address must be at least 5 characters')
    .max(200, 'Company address must be less than 200 characters'),
  companyAddressPlaceId: Yup.string().when('companyAddressLine1', {
    is: (val: string) => val && val.length > 0,
    then: (schema) =>
      schema.required('Please select a valid address from the suggestions'),
  }),
  companyAddressLine2: Yup.string().max(
    200,
    'Address line 2 must be less than 200 characters'
  ),
});

// Email OTP validation schema
export const emailOtpValidationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Please enter a valid 6-digit code'),
});

// Update email validation schema
export const updateEmailValidationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

// Update phone validation schema
export const updatePhoneValidationSchema = Yup.object({
  phone: Yup.string()
    .required('Phone number is required')
    .test(
      'phone-format',
      'Please enter a valid 10-digit phone number',
      (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, '');
        return digits.length === 10;
      }
    ),
});

// Initial values for forms
export const initialValues = {
  phone: '',
  code: '',
  userType: ZEME_USERS_ROLE.RENTER as UserType,
  firstName: '',
  lastName: '',
  email: '',
  consent: false,
};
